<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import addDays from 'date-fns/addDays';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import { getQuantileIntervals } from '@chatwoot/utils';
import reportsAPI from 'dashboard/api/reports';
import {
  downloadCsvFile,
  generateFileName,
} from 'dashboard/helper/downloadHelper';
import LineChart from 'shared/components/charts/LineChart.vue';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';
import { DATE_RANGE_OPTIONS } from './constants';

const TAGGED_METRIC_KEY = 'tagged_chats_count';
const NOT_TAGGED_METRIC_KEY = 'not_tagged_chats_count';
const HEATMAP_METRIC_KEY = TAGGED_METRIC_KEY;
const HEATMAP_QUANTILES = [0.2, 0.4, 0.6, 0.8, 0.9, 0.99];

const { t } = useI18n();

const filters = ref({
  from: 0,
  to: 0,
  userIds: [],
  labelId: null,
  inboxId: null,
  teamId: null,
  vipLevelId: null,
  issueId: null,
  priority: null,
});
const isHourlyDistributionEnabled = ref(false);
const isLoading = ref(false);
const isHeatmapLoading = ref(false);
const errorMessage = ref('');
const taggedSeries = ref([]);
const notTaggedSeries = ref([]);
const summary = ref({});
const tagRows = ref([]);
const heatmapSeries = ref([]);
const generatedAt = ref(null);

const SUPPORTED_RANGE_IDS = [
  DATE_RANGE_OPTIONS.TODAY.id,
  DATE_RANGE_OPTIONS.YESTERDAY.id,
  DATE_RANGE_OPTIONS.LAST_7_DAYS.id,
  DATE_RANGE_OPTIONS.LAST_30_DAYS.id,
  DATE_RANGE_OPTIONS.LAST_3_MONTHS.id,
  DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id,
];

const reportFilters = computed(() => ({
  userIds: filters.value.userIds,
  labelId: filters.value.labelId,
  teamId: filters.value.teamId,
  vipLevelId: filters.value.vipLevelId,
  issueId: filters.value.issueId,
  priority: filters.value.priority,
}));

const reportScope = computed(() => {
  const inboxId = Number(filters.value.inboxId || 0);
  if (Number.isFinite(inboxId) && inboxId > 0) {
    return { type: 'inbox', id: inboxId };
  }
  return { type: 'account', id: undefined };
});

const currentPeriod = computed(() => {
  const fromTs = Number(filters.value.from || 0);
  const toTs = Number(filters.value.to || 0);
  if (!fromTs || !toTs) {
    const todayStart = startOfDay(new Date());
    const todayEnd = endOfDay(new Date());
    return {
      from: 0,
      to: 0,
      fromDate: todayStart,
      toDate: todayEnd,
      days: 1,
    };
  }

  const fromDate = startOfDay(fromUnixTime(fromTs));
  const toDate = endOfDay(fromUnixTime(toTs));
  const days = Math.max(differenceInCalendarDays(toDate, fromDate) + 1, 1);

  return {
    from: getUnixTime(fromDate),
    to: getUnixTime(toDate),
    fromDate,
    toDate,
    days,
  };
});

const currentPeriodLabel = computed(() => {
  if (!currentPeriod.value.from || !currentPeriod.value.to) return '-';
  return `${format(fromUnixTime(currentPeriod.value.from), 'dd MMM yy')} - ${format(
    fromUnixTime(currentPeriod.value.to),
    'dd MMM yy'
  )}`;
});

const normalizeSeries = series => {
  if (!Array.isArray(series)) return [];
  return [...series].sort((left, right) => left.timestamp - right.timestamp);
};

const sumSeries = series =>
  series.reduce((sum, item) => sum + Number(item.value || 0), 0);

const dayColumns = computed(() => {
  return Array.from({ length: currentPeriod.value.days }, (_, index) => {
    const date = addDays(currentPeriod.value.fromDate, index);
    return {
      key: format(date, 'yyyy-MM-dd'),
      timestamp: getUnixTime(startOfDay(date)),
      label: format(date, 'dd MMM'),
      tooltipLabel: format(date, 'dd MMM yy'),
    };
  });
});

const seriesToDateMap = series =>
  series.reduce((acc, item) => {
    const key = format(fromUnixTime(item.timestamp), 'yyyy-MM-dd');
    acc[key] = Number(item.value || 0);
    return acc;
  }, {});

const chartRows = computed(() => {
  const taggedMap = seriesToDateMap(taggedSeries.value);
  const notTaggedMap = seriesToDateMap(notTaggedSeries.value);

  return dayColumns.value.map(day => ({
    timestamp: day.timestamp,
    taggedValue: taggedMap[day.key] || 0,
    notTaggedValue: notTaggedMap[day.key] || 0,
    label: day.label,
  }));
});

const taggedChatsCount = computed(() => {
  const value = Number(summary.value?.tagged_chats_count || 0);
  return value || sumSeries(taggedSeries.value);
});

const notTaggedChatsCount = computed(() => {
  const value = Number(summary.value?.not_tagged_chats_count || 0);
  return value || sumSeries(notTaggedSeries.value);
});

const totalChatsCount = computed(
  () => taggedChatsCount.value + notTaggedChatsCount.value
);

const taggedPercentage = computed(() => {
  const value = summary.value?.tagged_percentage;
  if (value !== undefined && value !== null) return Number(value).toFixed(2);
  if (!totalChatsCount.value) return '0.00';
  return ((taggedChatsCount.value / totalChatsCount.value) * 100).toFixed(2);
});

const notTaggedPercentage = computed(() => {
  const value = summary.value?.not_tagged_percentage;
  if (value !== undefined && value !== null) return Number(value).toFixed(2);
  if (!totalChatsCount.value) return '0.00';
  return ((notTaggedChatsCount.value / totalChatsCount.value) * 100).toFixed(2);
});

const chartCollection = computed(() => ({
  labels: chartRows.value.map(row => row.label),
  datasets: [
    {
      label: t('TAGS_USAGE_REPORT.TAGGED_CHATS'),
      data: chartRows.value.map(row => row.taggedValue),
      borderColor: '#1D75E5',
      backgroundColor: '#1D75E5',
      pointBackgroundColor: '#1D75E5',
      pointRadius: 3,
      pointHoverRadius: 4,
      tension: 0.25,
      fill: false,
    },
    {
      label: t('TAGS_USAGE_REPORT.NOT_TAGGED_CHATS'),
      data: chartRows.value.map(row => row.notTaggedValue),
      borderColor: '#E7C51B',
      backgroundColor: '#E7C51B',
      pointBackgroundColor: '#E7C51B',
      pointRadius: 3,
      pointHoverRadius: 4,
      tension: 0.25,
      fill: false,
    },
  ],
}));

const chartOptions = {
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: value => Number(value).toLocaleString(),
      },
    },
  },
};

const hourlyRows = computed(() => {
  return Array.from({ length: 24 }, (_, hour) => hour);
});

const heatmapValueMap = computed(() => {
  return heatmapSeries.value.reduce((acc, item) => {
    const date = fromUnixTime(item.timestamp);
    const dayKey = format(date, 'yyyy-MM-dd');
    const key = `${dayKey}-${date.getHours()}`;
    acc[key] = Number(item.value || 0);
    return acc;
  }, {});
});

const heatmapRows = computed(() => {
  return hourlyRows.value.map(hour => {
    return {
      hour,
      cells: dayColumns.value.map(day => {
        const key = `${day.key}-${hour}`;
        const value = heatmapValueMap.value[key] || 0;
        return {
          key,
          value,
          dayLabel: day.tooltipLabel,
        };
      }),
    };
  });
});

const heatmapValues = computed(() =>
  Object.values(heatmapValueMap.value).map(value => Number(value || 0))
);

const heatmapQuantiles = computed(() =>
  getQuantileIntervals(heatmapValues.value, HEATMAP_QUANTILES)
);

const getHeatmapCellClass = value => {
  if (!value) return 'bg-n-slate-2';

  let level = [...heatmapQuantiles.value, Infinity].findIndex(
    range => value <= range && value > 0
  );

  if (level > 6) level = 5;
  if (level === 0) return 'bg-n-slate-2';

  const classes = [
    'bg-n-blue-3',
    'bg-n-blue-5',
    'bg-n-blue-7',
    'bg-n-blue-8',
    'bg-n-blue-10',
    'bg-n-blue-11',
  ];

  return classes[level - 1];
};

const formatHourLabel = hour => {
  const hour12 = hour % 12 || 12;
  const suffix = hour < 12 ? 'am' : 'pm';
  return `${hour12}:00 ${suffix}`;
};

const generatedAtLabel = computed(() => {
  if (!generatedAt.value) {
    return t('LAST_7_DAYS_REPORT.NOT_GENERATED_YET');
  }

  return generatedAt.value.toLocaleString();
});

const toCsvValue = value => {
  const stringValue = String(value ?? '');
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const rowsToCsv = (headers, rows) => {
  const csvRows = [headers, ...rows];
  return csvRows
    .map(row => row.map(column => toCsvValue(column)).join(','))
    .join('\n');
};

const downloadBreakdownCsv = () => {
  const headers = [
    t('TAGS_USAGE_REPORT.COLUMN_TAG'),
    t('TAGS_USAGE_REPORT.COLUMN_CHAT_COUNT'),
    t('TAGS_USAGE_REPORT.COLUMN_PERCENT_TAGGED'),
  ];

  const rows = tagRows.value.map(row => [
    row.tag,
    row.chats_count,
    `${Number(row.tagged_percentage || 0).toFixed(2)}%`,
  ]);

  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'tags-usage-breakdown',
    to: currentPeriod.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const downloadHeatmapCsv = () => {
  const headers = [
    t('TOTAL_CHATS_REPORT.HOUR'),
    ...dayColumns.value.map(day => day.label),
  ];
  const rows = heatmapRows.value.map(row => [
    formatHourLabel(row.hour),
    ...row.cells.map(cell => cell.value),
  ]);

  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'tags-usage-heatmap',
    to: currentPeriod.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const fetchTagsUsageData = async () => {
  if (!currentPeriod.value.from || !currentPeriod.value.to) return;
  isLoading.value = true;
  errorMessage.value = '';

  try {
    const [taggedResponse, notTaggedResponse, tagsUsageResponse] =
      await Promise.all([
        reportsAPI.getReports({
          metric: TAGGED_METRIC_KEY,
          from: currentPeriod.value.from,
          to: currentPeriod.value.to,
          type: reportScope.value.type,
          id: reportScope.value.id,
          groupBy: 'day',
          businessHours: false,
          ...reportFilters.value,
        }),
        reportsAPI.getReports({
          metric: NOT_TAGGED_METRIC_KEY,
          from: currentPeriod.value.from,
          to: currentPeriod.value.to,
          type: reportScope.value.type,
          id: reportScope.value.id,
          groupBy: 'day',
          businessHours: false,
          ...reportFilters.value,
        }),
        reportsAPI.getTagsUsage({
          from: currentPeriod.value.from,
          to: currentPeriod.value.to,
          userIds: filters.value.userIds,
          labelId: filters.value.labelId,
          inboxId: filters.value.inboxId,
          teamId: filters.value.teamId,
          vipLevelId: filters.value.vipLevelId,
          issueId: filters.value.issueId,
          priority: filters.value.priority,
        }),
      ]);

    taggedSeries.value = normalizeSeries(taggedResponse.data);
    notTaggedSeries.value = normalizeSeries(notTaggedResponse.data);
    summary.value = tagsUsageResponse.data?.summary || {};
    tagRows.value = tagsUsageResponse.data?.items || [];
    generatedAt.value = new Date();
  } catch {
    errorMessage.value = t('TAGS_USAGE_REPORT.FETCH_ERROR');
    taggedSeries.value = [];
    notTaggedSeries.value = [];
    summary.value = {};
    tagRows.value = [];
  } finally {
    isLoading.value = false;
  }
};

const fetchHeatmapData = async () => {
  if (!currentPeriod.value.from || !currentPeriod.value.to) return;
  isHeatmapLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await reportsAPI.getReports({
      metric: HEATMAP_METRIC_KEY,
      from: currentPeriod.value.from,
      to: currentPeriod.value.to,
      type: reportScope.value.type,
      id: reportScope.value.id,
      groupBy: 'hour',
      businessHours: false,
      ...reportFilters.value,
    });

    heatmapSeries.value = normalizeSeries(response.data);
    generatedAt.value = new Date();
  } catch {
    errorMessage.value = t('TAGS_USAGE_REPORT.FETCH_ERROR');
    heatmapSeries.value = [];
  } finally {
    isHeatmapLoading.value = false;
  }
};

const refreshData = async () => {
  await fetchTagsUsageData();
  if (isHourlyDistributionEnabled.value) {
    await fetchHeatmapData();
  }
};

watch(isHourlyDistributionEnabled, async enabled => {
  if (!enabled) return;
  await fetchHeatmapData();
});

const onFilterChange = ({
  from,
  to,
  selectedAgents = [],
  selectedLabel = null,
  selectedInbox = null,
  selectedTeam = null,
  selectedVip = null,
  selectedIssue = null,
  selectedPriority = null,
}) => {
  filters.value = {
    from,
    to,
    userIds: Array.isArray(selectedAgents)
      ? selectedAgents.map(agent => agent.id)
      : [],
    labelId: selectedLabel?.id || null,
    inboxId: selectedInbox?.id || null,
    teamId: selectedTeam?.id || null,
    vipLevelId: selectedVip?.id || null,
    issueId: selectedIssue?.id || null,
    priority: selectedPriority?.id || null,
  };
  refreshData();
};

onMounted(async () => {
  // Initial fetch happens when filters emit their initial value.
});
</script>

<template>
  <div class="tu-page">
    <div class="tu-shell">
      <div class="tu-header">
        <div class="tu-header-left">
          <h1 class="tu-title">{{ t('TAGS_USAGE_REPORT.TITLE') }}</h1>
        </div>
      </div>

      <div class="tu-toolbar">
        <CompactDateInboxFilters
          :allowed-date-range-ids="SUPPORTED_RANGE_IDS"
          show-agents
          show-labels
          show-teams
          show-vip
          show-issue
          show-priority
          hide-inactive-filters
          @filter-change="onFilterChange"
        />
        <div class="tu-toolbar-right">
          <span class="tu-toggle-label">
            {{ t('TOTAL_CHATS_REPORT.TWENTY_FOUR_HOUR_DISTRIBUTION') }}
          </span>
          <button
            class="crt-switch"
            :class="{ 'is-on': isHourlyDistributionEnabled }"
            type="button"
            @click="isHourlyDistributionEnabled = !isHourlyDistributionEnabled"
          >
            <span />
          </button>
        </div>
      </div>

      <div class="tu-content">
        <div v-if="errorMessage" class="tu-error">
          {{ errorMessage }}
        </div>

        <section class="tu-card">
          <div class="tu-card-header">
            <div class="tu-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="tu-card-title">{{ t('TAGS_USAGE_REPORT.TITLE') }}</h2>
              <span class="tu-period">{{ currentPeriodLabel }}</span>
            </div>
          </div>

          <div
            class="tu-section"
            :class="{ 'tu-section--no-bottom': !isHourlyDistributionEnabled }"
          >
            <div v-if="!isHourlyDistributionEnabled" class="tu-stack">
              <div class="tu-metric-row">
                <div class="tu-metric">
                  <div class="tu-metric-label">
                    {{ t('TAGS_USAGE_REPORT.TAGGED_CHATS') }}
                  </div>
                  <div class="tu-metric-value-row">
                    <div class="tu-metric-value">
                      {{ taggedChatsCount.toLocaleString() }}
                    </div>
                    <div class="tu-metric-badge">
                      {{ `${taggedPercentage}%` }}
                    </div>
                  </div>
                </div>
                <div class="tu-metric">
                  <div class="tu-metric-label">
                    {{ t('TAGS_USAGE_REPORT.NOT_TAGGED_CHATS') }}
                  </div>
                  <div class="tu-metric-value-row">
                    <div class="tu-metric-value">
                      {{ notTaggedChatsCount.toLocaleString() }}
                    </div>
                    <div class="tu-metric-badge">
                      {{ `${notTaggedPercentage}%` }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="tu-chart">
                <woot-loading-state
                  v-if="isLoading"
                  :message="$t('REPORT.LOADING_CHART')"
                  class="h-full"
                />
                <div v-else-if="!chartRows.length" class="tu-empty">
                  {{ t('TAGS_USAGE_REPORT.NO_DATA') }}
                </div>
                <LineChart
                  v-else
                  :collection="chartCollection"
                  :chart-options="chartOptions"
                />
              </div>
            </div>

            <div v-else class="tu-stack">
              <div class="tu-heatmap-title">
                <div class="tu-heatmap-h">
                  {{ t('TAGS_USAGE_REPORT.HEATMAP_TITLE') }}
                </div>
                <div class="tu-heatmap-sub">
                  {{ t('TAGS_USAGE_REPORT.HEATMAP_LIMITATION') }}
                </div>
              </div>

              <div class="tu-heatmap-wrap">
                <button
                  class="tu-export-btn"
                  type="button"
                  @click="downloadHeatmapCsv"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                    />
                  </svg>
                  <span>{{ t('TAGS_USAGE_REPORT.EXPORT_CSV') }}</span>
                </button>

                <div class="tu-heatmap-table-wrap">
                  <woot-loading-state
                    v-if="isHeatmapLoading"
                    :message="$t('REPORT.LOADING_CHART')"
                  />

                  <table
                    v-else-if="heatmapRows.length"
                    class="tu-heatmap-table"
                  >
                    <thead>
                      <tr>
                        <th class="tu-heatmap-th tu-heatmap-th--hour">
                          {{ t('TOTAL_CHATS_REPORT.HOUR') }}
                        </th>
                        <th
                          v-for="day in dayColumns"
                          :key="day.key"
                          class="tu-heatmap-th"
                        >
                          {{ day.label }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in heatmapRows" :key="`hour-${row.hour}`">
                        <td class="tu-heatmap-td tu-heatmap-td--hour">
                          {{
                            row.hour % 3 === 0 ? formatHourLabel(row.hour) : ''
                          }}
                        </td>
                        <td
                          v-for="cell in row.cells"
                          :key="cell.key"
                          class="tu-heatmap-td"
                          :title="`${cell.dayLabel} ${formatHourLabel(row.hour)}: ${cell.value}`"
                        >
                          <div
                            class="tu-heatmap-cell"
                            :class="getHeatmapCellClass(cell.value)"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <div v-else class="tu-empty">
                    {{ t('TAGS_USAGE_REPORT.NO_DATA') }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!isHourlyDistributionEnabled" class="tu-breakdown">
            <div class="tu-breakdown-header">
              <div class="tu-breakdown-title">
                {{ t('TAGS_USAGE_REPORT.BREAKDOWN_TITLE') }}
              </div>
              <button
                class="tu-export-btn"
                type="button"
                @click="downloadBreakdownCsv"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                  />
                </svg>
                <span>{{ t('TAGS_USAGE_REPORT.EXPORT_CSV') }}</span>
              </button>
            </div>

            <div class="tu-table-wrap">
              <table class="tu-table">
                <thead>
                  <tr>
                    <th class="tu-th-tag">
                      {{ t('TAGS_USAGE_REPORT.COLUMN_TAG') }}
                    </th>
                    <th class="tu-th-num">
                      {{ t('TAGS_USAGE_REPORT.COLUMN_CHAT_COUNT') }}
                    </th>
                    <th class="tu-th-num">
                      {{ t('TAGS_USAGE_REPORT.COLUMN_PERCENT_TAGGED') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in tagRows" :key="row.tag_id">
                    <td class="tu-td-tag">{{ row.tag }}</td>
                    <td class="tu-td-num tu-td-num--link">
                      {{ Number(row.chats_count || 0).toLocaleString() }}
                    </td>
                    <td class="tu-td-num">
                      {{ `${Number(row.tagged_percentage || 0).toFixed(2)}%` }}
                    </td>
                  </tr>
                  <tr v-if="!tagRows.length">
                    <td colspan="3" class="tu-empty-row">
                      {{ t('TAGS_USAGE_REPORT.NO_DATA') }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <p class="tu-footer-note">
          {{ t('TAGS_USAGE_REPORT.GENERATED_AT', { time: generatedAtLabel })
          }}<br />
          {{ t('LAST_7_DAYS_REPORT.LOCAL_TIME_NOTE') }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Styling aligned to Staffing prediction layout */
.tu-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  background: #fff;
  color: #111827;
}
.tu-shell {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  overflow: visible;
  min-width: 650px;
}
.tu-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
  position: sticky;
  top: 0;
  z-index: 50;
  background: #ffffff;
}
.tu-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}
.tu-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}
.tu-toolbar {
  padding: 12px 24px 8px;
  background: #fff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  position: sticky;
  top: 58px;
  z-index: 49;
}
.tu-toolbar-right {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.tu-toggle-label {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}
.crt-switch {
  width: 27px;
  height: 20px;
  border: none;
  border-radius: 15px;
  box-sizing: border-box;
  cursor: pointer;
  background: rgb(221, 221, 221);
  padding: 0;
  display: block;
  position: relative;
}
.crt-switch span {
  position: absolute;
  display: block;
  top: 2px;
  left: 2px;
  border-radius: 50%;
  background-color: #fff;
  box-shadow:
    rgba(0, 0, 0, 0.05) 0 3px 3px 0,
    rgba(0, 0, 0, 0.1) 0 2px 2px 0,
    rgba(0, 0, 0, 0.05) 0 3px 2px 0;
  transition:
    left 0.2s ease-in-out,
    background-color 0.2s ease-in-out;
  will-change: left, background-color;
  width: 15px;
  height: 15px;
}
.crt-switch.is-on {
  background: #2f80ed;
}
.crt-switch.is-on span {
  left: 10px;
}
.tu-content {
  padding: 16px 32px;
}
.tu-error {
  margin-bottom: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}
.tu-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #fff;
  box-sizing: border-box;
  margin-bottom: 20px;
  overflow: hidden;
}
.tu-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e5e7eb;
}
.tu-card-title-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.tu-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}
.tu-period {
  font-size: 12px;
  color: #6b7280;
  margin-left: 10px;
  white-space: nowrap;
}
.tu-section {
  padding: 16px 20px 20px;
}
.tu-section--no-bottom {
  padding-bottom: 0;
}
.tu-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.tu-metric-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.tu-metric {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
}
.tu-metric-label {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.tu-metric-value-row {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.tu-metric-value {
  font-size: 28px;
  line-height: 32px;
  font-weight: 700;
  color: #111827;
}
.tu-metric-badge {
  border-radius: 6px;
  background: #e5e7eb;
  color: #374151;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 6px;
}
.tu-chart {
  height: 384px;
}
.tu-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}
.tu-breakdown {
  border-top: 1px solid #dfe3e8;
}
.tu-breakdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  padding: 15px 16px;
}
.tu-breakdown-title {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}
.tu-export-btn {
  min-width: 128px;
  height: 36px;
  padding: 0 16px 0 12px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  background: #fff;
  color: #111827;
  font-weight: 600;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  white-space: nowrap;
}
.tu-export-btn:hover {
  background: #f9fafb;
}
.tu-table-wrap {
  overflow-x: auto;
  border-top: 1px solid #dfe3e8;
}
.tu-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.tu-table thead th {
  background: #f8fafc;
  color: #424d57;
  text-align: left;
  font-weight: 600;
  border-bottom: 1px solid #dfe3e8;
  padding: 10px 12px;
}
.tu-table tbody td {
  border-bottom: 1px solid #edf1f5;
  padding: 10px 12px;
}
.tu-td-num {
  text-align: right;
  font-weight: 600;
  color: #111827;
}
.tu-td-num--link {
  color: #1d75e5;
}
.tu-empty-row {
  padding: 24px 12px;
  text-align: center;
  color: #6b7280;
}
.tu-heatmap-title {
  margin-bottom: 4px;
}
.tu-heatmap-h {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}
.tu-heatmap-sub {
  margin-top: 4px;
  font-size: 13px;
  color: #b91c1c;
}
.tu-heatmap-wrap {
  overflow-x: auto;
}
.tu-heatmap-table-wrap {
  margin-top: 12px;
  overflow-x: auto;
}
.tu-heatmap-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 4px;
}
.tu-heatmap-th {
  padding: 4px 6px;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: #111827;
}
.tu-heatmap-th--hour {
  text-align: left;
  color: #6b7280;
}
.tu-heatmap-td {
  padding: 2px;
}
.tu-heatmap-td--hour {
  font-size: 12px;
  color: #6b7280;
  padding-right: 10px;
  white-space: nowrap;
}
.tu-heatmap-cell {
  height: 24px;
  width: 56px;
  border-radius: 4px;
}
.tu-footer-note {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}
::global(body.dark) .tu-page,
::global(body.dark) .tu-shell,
::global(body.dark) .tu-card,
::global(body.dark) .tu-header,
::global(body.dark) .tu-toolbar,
::global(body.dark) .tu-table thead th,
::global(body.dark) .tu-table tbody td,
::global(body.dark) .tu-heatmap-th,
::global(body.dark) .tu-heatmap-td,
::global(body.theme-mix) .tu-page,
::global(body.theme-mix) .tu-shell,
::global(body.theme-mix) .tu-card,
::global(body.theme-mix) .tu-header,
::global(body.theme-mix) .tu-toolbar,
::global(body.theme-mix) .tu-table thead th,
::global(body.theme-mix) .tu-table tbody td,
::global(body.theme-mix) .tu-heatmap-th,
::global(body.theme-mix) .tu-heatmap-td {
  background: #fff !important;
  color: #111827 !important;
}

::global(body.dark) .tu-table thead th *,
::global(body.dark) .tu-heatmap-th *,
::global(body.theme-mix) .tu-table thead th *,
::global(body.theme-mix) .tu-heatmap-th * {
  color: #111827 !important;
}
</style>
